import React, { useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Checkbox,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Icon } from "@iconify/react";
import {
  calculateBookingPricePreview,
  formatSessionDateTimeRange,
  formatCurrency,
  getPricingRuleById,
  getSortedSessions,
  resolveSessionId,
} from "./bookingPricingUtils";

const normalizeSessionIds = (sessionIds = []) => {
  const seen = new Set();
  const next = [];
  (Array.isArray(sessionIds) ? sessionIds : []).forEach((sessionId) => {
    const normalized = String(sessionId || "").trim();
    if (!normalized || seen.has(normalized)) return;
    seen.add(normalized);
    next.push(normalized);
  });
  return next;
};

function shuffle(array) {
  let currentIndex = array.length;

  // While there remain elements to shuffle...
  while (currentIndex !== 0) {
    // Pick a remaining element...
    let randomIndex = Math.floor(Math.random() * currentIndex);
    currentIndex--;

    // And swap it with the current element.
    [array[currentIndex], array[randomIndex]] = [
      array[randomIndex],
      array[currentIndex],
    ];
  }
  return array;
}

/**
 * Finds the cheapest pricing rule for a given session selection.
 * Tries every rule, calculates total, picks the lowest.
 */
const findBestPricingRule = ({
  event,
  sessionIds,
  participantCount,
  memberParticipantCount,
  bookingDate,
  pricingSnapshot,
  adminDiscount,
}) => {
  const pricingRules = event?.pricingRules || [];
  if (pricingRules.length === 0) return null;
  if (pricingRules.length === 1) return pricingRules[0];

  let bestRule = null;
  let bestTotal = Infinity;

  pricingRules.forEach((rule) => {
    const preview = calculateBookingPricePreview({
      event,
      selectedPricingRuleId: rule.ruleId,
      selectedSessionIds: sessionIds,
      participantCount,
      memberParticipantCount,
      bookingDate,
      pricingSnapshot,
      adminDiscount,
    });
    if (!preview || preview.calculationError) return;
    if (preview.totalAmount < bestTotal) {
      bestTotal = preview.totalAmount;
      bestRule = rule;
    }
  });

  return bestRule || pricingRules[0];
};

const RULE_TYPE_META = {
  per_session: {
    icon: "mdi:ticket-outline",
    getDescription: (rule) => `${formatCurrency(rule.amount)} pro Option`,
  },
  fixed: {
    icon: "mdi:tag-check-outline",
    getDescription: () => "Festpreis",
  },
  package: {
    icon: "mdi:package-variant-closed",
    getDescription: (rule) => {
      const min = Number(rule.minSessions) || 0;
      if (min > 0) return `Ab ${min} Option${min === 1 ? "" : "en"}`;
      const linked = rule.sessionIds?.length || 0;
      if (linked > 0)
        return `${linked} Option${linked === 1 ? "" : "en"} im Paket`;
      return "Paketpreis";
    },
  },
};

export default function BookingStepSessions({
  event,
  selectedSessionIds,
  selectedPricingRuleId,
  participantCount,
  memberParticipantCount,
  bookingDate,
  pricingSnapshot,
  adminDiscount,
  onSessionsChange,
  onPricingRuleChange,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const sessions = useMemo(
    () => (Array.isArray(event?.sessions) ? event.sessions : []),
    [event?.sessions],
  );
  const sortedSessions = useMemo(() => getSortedSessions(sessions), [sessions]);
  const pricingRules = useMemo(
    () => (Array.isArray(event?.pricingRules) ? event.pricingRules : []),
    [event?.pricingRules],
  );
  const selectedRule = useMemo(
    () => getPricingRuleById(pricingRules, selectedPricingRuleId),
    [pricingRules, selectedPricingRuleId],
  );
  const safeSelectedSessionIds = useMemo(
    () => normalizeSessionIds(selectedSessionIds),
    [selectedSessionIds],
  );
  const isPackageRuleSelected = selectedRule?.type === "package";
  const packageMinSessions = Number(selectedRule?.minSessions) || 0;
  const isMinSessionsPackage = isPackageRuleSelected && packageMinSessions > 0;
  const allSessionIds = useMemo(
    () =>
      sortedSessions
        .map((session, index) => resolveSessionId(session, `session-${index}`))
        .filter(Boolean),
    [sortedSessions],
  );
  // Auto-select best pricing rule when sessions change
  const autoSelectBestRule = useCallback(
    (nextSessionIds) => {
      if (pricingRules.length <= 1) return;
      const bestRule = findBestPricingRule({
        event,
        sessionIds: nextSessionIds,
        participantCount,
        memberParticipantCount,
        bookingDate,
        pricingSnapshot,
        adminDiscount,
      });
      if (bestRule && bestRule.ruleId !== selectedPricingRuleId) {
        onPricingRuleChange(bestRule.ruleId);
      }
    },
    [
      adminDiscount,
      bookingDate,
      event,
      memberParticipantCount,
      onPricingRuleChange,
      participantCount,
      pricingRules.length,
      pricingSnapshot,
      selectedPricingRuleId,
    ],
  );

  // Auto-select best rule on mount and when sessions change
  useEffect(() => {
    if (safeSelectedSessionIds.length > 0 && pricingRules.length > 1) {
      autoSelectBestRule(safeSelectedSessionIds);
    }
  }, [safeSelectedSessionIds, pricingRules.length, autoSelectBestRule]);

  const toggleSession = (sessionId) => {
    const currentSet = new Set(safeSelectedSessionIds);
    if (currentSet.has(sessionId)) {
      currentSet.delete(sessionId);
    } else {
      currentSet.add(sessionId);
    }

    const nextSessionIds = allSessionIds.filter((id) => currentSet.has(id));
    onSessionsChange(nextSessionIds);

    // Auto-select best pricing rule for new selection
    if (pricingRules.length > 1) {
      const bestRule = findBestPricingRule({
        event,
        sessionIds: nextSessionIds,
        participantCount,
        memberParticipantCount,
        bookingDate,
        pricingSnapshot,
        adminDiscount,
      });
      if (bestRule && bestRule.ruleId !== selectedPricingRuleId) {
        onPricingRuleChange(bestRule.ruleId);
      }
    }
  };

  // Click a pricing rule card → auto-select matching options; click again → deselect
  const handleRuleCardClick = (rule) => {
    const isActive =
      rule.ruleId === selectedPricingRuleId &&
      safeSelectedSessionIds.length > 0;

    if (isActive) {
      onSessionsChange([]);
      return;
    }

    onPricingRuleChange(rule.ruleId);

    if (rule.type === "fixed") {
      onSessionsChange(allSessionIds);
    } else if (rule.type === "package") {
      const minSessions = Number(rule.minSessions) || 0;
      if (minSessions > 0) {
        // Shuffle to avoid collisions with linked-session rules on other packages
        const shuffled = shuffle([...allSessionIds]);
        onSessionsChange(
          shuffled.slice(0, Math.min(minSessions, shuffled.length)),
        );
      } else if (rule.sessionIds?.length > 0) {
        const linkedIds = rule.sessionIds
          .map((id) => String(id).trim())
          .filter(Boolean);
        onSessionsChange(allSessionIds.filter((id) => linkedIds.includes(id)));
      }
    }
    // per_session: only set the rule, user picks options individually
  };

  const pricePreview = useMemo(
    () =>
      calculateBookingPricePreview({
        event,
        selectedPricingRuleId,
        selectedSessionIds: safeSelectedSessionIds,
        participantCount,
        memberParticipantCount,
        bookingDate,
        pricingSnapshot,
        adminDiscount,
      }),
    [
      adminDiscount,
      bookingDate,
      event,
      memberParticipantCount,
      participantCount,
      pricingSnapshot,
      selectedPricingRuleId,
      safeSelectedSessionIds,
    ],
  );

  // Package savings hint: check if selecting more options would unlock a cheaper package
  const packageSavingsHint = useMemo(() => {
    if (safeSelectedSessionIds.length === 0) return null;
    if (pricingRules.length === 0) return null;

    const packageRules = pricingRules.filter((r) => r.type === "package");
    if (packageRules.length === 0) return null;

    for (const pkgRule of packageRules) {
      const minSessions = Number(pkgRule.minSessions) || 0;
      if (minSessions <= 0) continue;

      const remaining = minSessions - safeSelectedSessionIds.length;
      if (remaining <= 0 || remaining > 3) continue;

      const pkgPreview = calculateBookingPricePreview({
        event,
        selectedPricingRuleId: pkgRule.ruleId,
        selectedSessionIds: allSessionIds.slice(0, minSessions),
        participantCount,
        memberParticipantCount,
        bookingDate,
        pricingSnapshot,
        adminDiscount,
      });
      if (pkgPreview && !pkgPreview.calculationError) {
        return {
          remaining,
          packageLabel: pkgRule.label,
          packagePrice: pkgRule.amount,
        };
      }
    }
    return null;
  }, [
    adminDiscount,
    allSessionIds,
    bookingDate,
    event,
    memberParticipantCount,
    participantCount,
    pricingRules,
    pricingSnapshot,
    safeSelectedSessionIds,
  ]);

  const hasEarlyBirdDiscountConfigured =
    pricePreview &&
    (pricePreview.earlyBirdDiscountType === "fixed"
      ? pricePreview.earlyBirdDiscountFixedAmount > 0
      : pricePreview.earlyBirdDiscountPercent > 0);

  const renderPricingRuleCards = () => {
    if (pricingRules.length === 0) return null;

    // Mobile: horizontal scrollable compact chips
    if (isMobile) {
      return (
        <Stack
          direction="row"
          spacing={1}
          sx={{
            mb: 2,
            overflowX: "auto",
            pb: 0.5,
            mx: -0.5,
            px: 0.5,
            WebkitOverflowScrolling: "touch",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          {pricingRules.map((rule) => {
            const isActive =
              rule.ruleId === selectedPricingRuleId &&
              safeSelectedSessionIds.length > 0;
            return (
              <Chip
                key={rule.ruleId}
                label={`${rule.label} · ${formatCurrency(rule.amount)}`}
                color={isActive ? "primary" : "default"}
                variant={isActive ? "filled" : "outlined"}
                onClick={() => handleRuleCardClick(rule)}
                icon={
                  isActive ? (
                    <Icon icon="mdi:check-circle" width={16} />
                  ) : undefined
                }
                sx={{
                  whiteSpace: "nowrap",
                  height: 36,
                  fontWeight: isActive ? 600 : 400,
                  "& .MuiChip-label": { px: 1.5 },
                }}
              />
            );
          })}
        </Stack>
      );
    }

    // Desktop: full cards
    return (
      <Stack
        direction="row"
        spacing={1.5}
        sx={{ mb: 2.5 }}
        flexWrap="wrap"
        useFlexGap
      >
        {pricingRules.map((rule) => {
          const isActive =
            rule.ruleId === selectedPricingRuleId &&
            safeSelectedSessionIds.length > 0;
          const meta =
            RULE_TYPE_META[rule.type] || RULE_TYPE_META.per_session;

          return (
            <Paper
              key={rule.ruleId}
              variant="outlined"
              onClick={() => handleRuleCardClick(rule)}
              sx={{
                p: 1.5,
                flex: "1 1 0",
                minWidth: 140,
                cursor: "pointer",
                userSelect: "none",
                position: "relative",
                transition: "all 0.2s",
                borderColor: isActive ? "primary.main" : "divider",
                borderWidth: isActive ? 2 : 1,
                bgcolor: isActive
                  ? "rgba(25, 118, 210, 0.06)"
                  : "background.paper",
                "&:hover": {
                  borderColor: "primary.light",
                  bgcolor: "rgba(25, 118, 210, 0.04)",
                },
              }}
            >
              {isActive && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 6,
                    right: 6,
                    width: 20,
                    height: 20,
                    borderRadius: "50%",
                    bgcolor: "primary.main",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Icon icon="mdi:check" width={14} color="#fff" />
                </Box>
              )}
              <Stack spacing={0.5} alignItems="center" textAlign="center">
                <Icon
                  icon={meta.icon}
                  width={24}
                  color={isActive ? "#1976d2" : "#757575"}
                />
                <Typography variant="subtitle2" sx={{ lineHeight: 1.3 }}>
                  {rule.label}
                </Typography>
                <Typography
                  variant="h6"
                  color="primary.main"
                  fontWeight={700}
                  sx={{ lineHeight: 1.2 }}
                >
                  {formatCurrency(rule.amount)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {meta.getDescription(rule)}
                </Typography>
              </Stack>
            </Paper>
          );
        })}
      </Stack>
    );
  };

  const renderAlerts = () => (
    <>
      {/* Package savings hint */}
      {packageSavingsHint && !isPackageRuleSelected && (
        <Alert
          severity="success"
          icon={<Icon icon="mdi:tag-outline" width={20} />}
          sx={{ mb: isMobile ? 1.5 : 2 }}
        >
          {isMobile ? (
            <Typography variant="body2">
              Noch {packageSavingsHint.remaining} Option
              {packageSavingsHint.remaining === 1 ? "" : "en"} für Paketpreis{" "}
              <strong>{formatCurrency(packageSavingsHint.packagePrice)}</strong>
            </Typography>
          ) : (
            <>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Spartipp: Paketpreis verfügbar!
              </Typography>
              <Typography variant="body2">
                Noch {packageSavingsHint.remaining} Option
                {packageSavingsHint.remaining === 1 ? "" : "en"} auswählen und den
                Paketpreis von{" "}
                <strong>{formatCurrency(packageSavingsHint.packagePrice)}</strong>{" "}
                ({packageSavingsHint.packageLabel}) nutzen.
              </Typography>
            </>
          )}
        </Alert>
      )}

      {/* Active package confirmation */}
      {isMinSessionsPackage &&
        pricePreview &&
        !pricePreview.calculationError &&
        (pricePreview.packageCount || 0) > 0 &&
        pricePreview.remainderSessionCount === 0 && (
          <Alert
            severity="success"
            icon={<Icon icon="mdi:check-circle-outline" width={20} />}
            sx={{ mb: isMobile ? 1.5 : 2 }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Paketpreis aktiv
            </Typography>
            {!isMobile && (
              <Typography variant="body2">
                {pricePreview.packageCount} Paket
                {pricePreview.packageCount === 1 ? "" : "e"} (
                {selectedRule?.label}){" "}
                {pricePreview.packageCount === 1 ? "wird" : "werden"} angewendet.
              </Typography>
            )}
          </Alert>
        )}

      {/* Min-sessions package: remaining hint */}
      {isMinSessionsPackage &&
        safeSelectedSessionIds.length > 0 &&
        pricePreview?.remainderSessionCount > 0 && (
          <Alert
            severity="info"
            icon={<Icon icon="mdi:information-outline" width={20} />}
            sx={{ mb: isMobile ? 1.5 : 2 }}
          >
            <Typography variant="body2">
              {pricePreview.nextPackageRemainingSessions > 0
                ? `Noch ${pricePreview.nextPackageRemainingSessions} Option${
                    pricePreview.nextPackageRemainingSessions === 1 ? "" : "en"
                  } für weiteres Paket.`
                : `${safeSelectedSessionIds.length} Option${
                    safeSelectedSessionIds.length === 1 ? "" : "en"
                  } ausgewählt.`}
            </Typography>
          </Alert>
        )}
    </>
  );

  const renderSessionList = () => {
    if (sortedSessions.length === 0) return null;

    return (
      <Stack spacing={isMobile ? 0.75 : 1} sx={{ mb: isMobile ? 1.5 : 3 }}>
        {sortedSessions.map((session, index) => {
          const sessionId = resolveSessionId(session, `session-${index}`);
          const checked = safeSelectedSessionIds.includes(sessionId);
          const sessionDateLabel = formatSessionDateTimeRange(session);

          return (
            <Paper
              key={sessionId}
              variant="outlined"
              sx={{
                p: isMobile ? 1 : 1.5,
                cursor: "pointer",
                userSelect: "none",
                transition: "border-color 0.15s, background-color 0.15s",
                borderColor: checked ? "primary.main" : "divider",
                bgcolor: checked
                  ? "rgba(25, 118, 210, 0.04)"
                  : "background.paper",
                "&:hover": {
                  borderColor: "primary.light",
                  bgcolor: "rgba(25, 118, 210, 0.06)",
                },
              }}
              onClick={() => toggleSession(sessionId)}
            >
              <Stack direction="row" alignItems="center" spacing={isMobile ? 0.75 : 1}>
                <Checkbox
                  checked={checked}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => toggleSession(sessionId)}
                  size={isMobile ? "small" : "medium"}
                  sx={{ p: 0.5 }}
                />
                <Stack spacing={0} sx={{ flex: 1, minWidth: 0 }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.75}
                    flexWrap="wrap"
                  >
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      sx={isMobile ? { fontSize: "0.825rem" } : undefined}
                    >
                      {session.label}
                    </Typography>
                    {session.maxParticipants && !isMobile && (
                      <Chip
                        label={`max. ${session.maxParticipants}`}
                        size="small"
                        variant="outlined"
                        sx={{ height: 20, fontSize: "0.7rem" }}
                      />
                    )}
                  </Stack>
                  {sessionDateLabel && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={isMobile ? { fontSize: "0.7rem" } : undefined}
                    >
                      {sessionDateLabel}
                    </Typography>
                  )}
                </Stack>
              </Stack>
            </Paper>
          );
        })}
      </Stack>
    );
  };

  const renderPriceOverview = () => {
    if (!pricePreview || safeSelectedSessionIds.length === 0) return null;

    // Mobile: compact inline summary
    if (isMobile) {
      return (
        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            bgcolor: "rgba(25, 118, 210, 0.03)",
            borderColor: "primary.light",
          }}
        >
          {pricePreview.calculationError ? (
            <Alert severity="error" sx={{ py: 0.5 }}>
              {pricePreview.calculationError}
            </Alert>
          ) : (
            <Stack spacing={0.5}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Icon icon="mdi:receipt-text-outline" width={16} color="#1976d2" />
                  <Typography variant="body2" color="text.secondary">
                    {safeSelectedSessionIds.length} Option
                    {safeSelectedSessionIds.length === 1 ? "" : "en"}
                  </Typography>
                </Stack>
                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                  color="primary.main"
                >
                  {formatCurrency(pricePreview.totalAmount)}
                </Typography>
              </Stack>

              {/* Show discount hints compactly */}
              {(pricePreview.memberDiscountAmount > 0 ||
                (pricePreview.siblingDiscountEligible && pricePreview.siblingDiscountAmount > 0) ||
                (pricePreview.earlyBirdDiscountEligible && pricePreview.earlyBirdDiscountAmount > 0)) && (
                <Stack direction="row" spacing={0.5} flexWrap="wrap">
                  {pricePreview.memberDiscountAmount > 0 && (
                    <Chip
                      size="small"
                      color="success"
                      variant="outlined"
                      label={`-${formatCurrency(pricePreview.memberDiscountAmount)} Mitglied`}
                      sx={{ height: 22, fontSize: "0.65rem" }}
                    />
                  )}
                  {pricePreview.siblingDiscountEligible && pricePreview.siblingDiscountAmount > 0 && (
                    <Chip
                      size="small"
                      color="success"
                      variant="outlined"
                      label={`-${formatCurrency(pricePreview.siblingDiscountAmount)} Geschwister`}
                      sx={{ height: 22, fontSize: "0.65rem" }}
                    />
                  )}
                  {pricePreview.earlyBirdDiscountEligible && pricePreview.earlyBirdDiscountAmount > 0 && (
                    <Chip
                      size="small"
                      color="success"
                      variant="outlined"
                      label={`-${formatCurrency(pricePreview.earlyBirdDiscountAmount)} Frühbucher`}
                      sx={{ height: 22, fontSize: "0.65rem" }}
                    />
                  )}
                </Stack>
              )}

              {pricePreview.rule && (
                <Typography variant="caption" color="text.secondary">
                  {pricePreview.rule.type === "package" && `Paket: ${pricePreview.rule.label}`}
                  {pricePreview.rule.type === "per_session" && `${formatCurrency(pricePreview.rule.amount)}/Option`}
                  {pricePreview.rule.type === "fixed" && `Festpreis: ${pricePreview.rule.label}`}
                </Typography>
              )}
            </Stack>
          )}
        </Paper>
      );
    }

    // Desktop: full price overview (unchanged)
    return (
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          bgcolor: "rgba(25, 118, 210, 0.02)",
          borderColor: "primary.light",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ mb: 1.5 }}
        >
          <Icon icon="mdi:receipt-text-outline" width={20} color="#1976d2" />
          <Typography variant="subtitle2">Ihre Preisübersicht</Typography>
        </Stack>

        {pricePreview.calculationError ? (
          <Alert severity="error" sx={{ mb: 1 }}>
            {pricePreview.calculationError}
          </Alert>
        ) : (
          <Stack spacing={0.5}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                {safeSelectedSessionIds.length} Option
                {safeSelectedSessionIds.length === 1 ? "" : "en"} x{" "}
                {participantCount} Teilnehmer
                {participantCount === 1 ? "" : ":"}
              </Typography>
              <Typography variant="body2">
                {formatCurrency(pricePreview.grossAmount)}
              </Typography>
            </Stack>

            {pricePreview.rule.memberDiscount > 0 &&
              pricePreview.memberDiscountAmount > 0 && (
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Mitgliederrabatt ({pricePreview.rule.memberDiscount}%):
                  </Typography>
                  <Typography
                    variant="body2"
                    color="success.main"
                    fontWeight={500}
                  >
                    -{formatCurrency(pricePreview.memberDiscountAmount)}
                  </Typography>
                </Stack>
              )}

            {pricePreview.siblingDiscountEligible &&
              pricePreview.siblingDiscountAmount > 0 && (
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Geschwisterrabatt ({pricePreview.siblingDiscountPercent}%):
                  </Typography>
                  <Typography
                    variant="body2"
                    color="success.main"
                    fontWeight={500}
                  >
                    -{formatCurrency(pricePreview.siblingDiscountAmount)}
                  </Typography>
                </Stack>
              )}

            {hasEarlyBirdDiscountConfigured && (
              <Stack direction="row" justifyContent="space-between">
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    Frühbucherrabatt:
                  </Typography>
                  {pricePreview.earlyBirdDiscountEligible && (
                    <Chip
                      size="small"
                      color="success"
                      label="aktiv"
                      sx={{ height: 18, fontSize: "0.65rem" }}
                    />
                  )}
                </Stack>
                <Typography
                  variant="body2"
                  color={
                    pricePreview.earlyBirdDiscountEligible
                      ? "success.main"
                      : "text.secondary"
                  }
                  fontWeight={500}
                >
                  {pricePreview.earlyBirdDiscountEligible
                    ? `-${formatCurrency(pricePreview.earlyBirdDiscountAmount)}`
                    : "nicht aktiv"}
                </Typography>
              </Stack>
            )}

            <Divider sx={{ my: 0.75 }} />

            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="body1" fontWeight={700}>
                Gesamtbetrag:
              </Typography>
              <Typography
                variant="body1"
                fontWeight={700}
                color="primary.main"
              >
                {formatCurrency(pricePreview.totalAmount)}
              </Typography>
            </Stack>

            {pricePreview.rule && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                {pricePreview.rule.type === "package" &&
                  `Paketpreis: ${pricePreview.rule.label}`}
                {pricePreview.rule.type === "per_session" &&
                  `Einzelpreis: ${formatCurrency(pricePreview.rule.amount)} pro Option`}
                {pricePreview.rule.type === "fixed" &&
                  `Festpreis: ${pricePreview.rule.label}`}
              </Typography>
            )}
          </Stack>
        )}
      </Paper>
    );
  };

  return (
    <Box>
      <Typography variant={isMobile ? "body2" : "subtitle2"} gutterBottom sx={{ fontWeight: 600 }}>
        Optionen auswählen
      </Typography>
      {!isMobile && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Wählen Sie die Optionen, an denen Sie teilnehmen möchten.
        </Typography>
      )}

      {/* Mobile: show selected count as quick status */}
      {isMobile && safeSelectedSessionIds.length > 0 && (
        <Chip
          size="small"
          color="primary"
          variant="outlined"
          label={`${safeSelectedSessionIds.length} von ${sortedSessions.length} ausgewählt`}
          sx={{ mb: 1.5 }}
        />
      )}

      {renderPricingRuleCards()}
      {renderAlerts()}
      {renderSessionList()}
      {renderPriceOverview()}
    </Box>
  );
}

BookingStepSessions.propTypes = {
  event: PropTypes.object.isRequired,
  selectedSessionIds: PropTypes.array.isRequired,
  selectedPricingRuleId: PropTypes.string.isRequired,
  participantCount: PropTypes.number.isRequired,
  memberParticipantCount: PropTypes.number.isRequired,
  bookingDate: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date),
  ]),
  pricingSnapshot: PropTypes.object,
  adminDiscount: PropTypes.shape({
    label: PropTypes.string,
    type: PropTypes.oneOf(["percentage", "fixed"]),
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  onSessionsChange: PropTypes.func.isRequired,
  onPricingRuleChange: PropTypes.func.isRequired,
};
